import sortBy from 'lodash/sortBy';
import { KeyedStreamItem } from 'communication/types/StreamElement';
import { TimeSeriesDisableRange } from 'utils/timeseries/TimeSeriesDisableRange';
import { getItemTime } from 'utils/timeseries/timeSeriesUtils';
import { timeUtils } from '@instinet-analytics/core-lib';

const { calculateMidTime, mapEpochToTime } = timeUtils;

export interface BucketedDataItem {
    startTime: number;
    midTime: number;
    endTime: number;
    endTimeDisplay: number;
}

export function createEmptyBuckets<T extends BucketedDataItem>(
    bucketSizeInMinutes: number,
    sortedData: KeyedStreamItem[],
    breaks: TimeSeriesDisableRange[],
    buildEmptyBucketData: (
        startTime: number,
        endTime: number
    ) => Partial<T> = () => ({}),
    suppressFirstBucketRounding = false
): T[] {
    const firstDataMinute = sortedData[0]._i;
    const firstBucketMinute = suppressFirstBucketRounding
        ? firstDataMinute
        : firstDataMinute - (firstDataMinute % bucketSizeInMinutes);
    const endMinutes = sortedData[sortedData.length - 1]._i;

    let bucketedData: T[] = [];
    let startOfBucket = firstBucketMinute;

    do {
        const startTime = mapEpochToTime(startOfBucket);
        const endTimeDisplay = mapEpochToTime(
            startOfBucket + bucketSizeInMinutes
        );
        const endMins = startOfBucket + bucketSizeInMinutes - 1;
        const endTime = mapEpochToTime(endMins);

        const item = buildEmptyBucket(
            startTime,
            endTime,
            endTimeDisplay,
            buildEmptyBucketData
        );

        bucketedData.push(item);

        startOfBucket = startOfBucket + bucketSizeInMinutes;
    } while (startOfBucket <= endMinutes);

    bucketedData = sortBy(bucketedData, 'startTime');

    // Ensure first bucket is in range...
    bucketedData[0].startTime = mapEpochToTime(sortedData[0]._i);
    calculateBucketMidTime(bucketedData[0]);

    bucketedData = applyBreaks(bucketedData, breaks, buildEmptyBucketData);

    return bucketedData;
}

function buildEmptyBucket<T extends BucketedDataItem>(
    startTime: number,
    endTime: number,
    endTimeDisplay: number,
    buildEmptyBucketData: (startTime: number, endTime: number) => Partial<T>
): T {
    const item = {
        ...buildEmptyBucketData(startTime, endTime),
        startTime,
        midTime: 0,
        endTime,
        endTimeDisplay,
    } as T;
    calculateBucketMidTime(item);
    return item;
}

function calculateBucketMidTime(b: BucketedDataItem) {
    b.midTime = calculateMidTime(b.startTime, b.endTime);
}

function applyBreaks<T extends BucketedDataItem>(
    bucketedData: T[],
    breaks: TimeSeriesDisableRange[],
    buildEmptyBucketData: (startTime: number, endTime: number) => Partial<T>
): T[] {
    for (const br of breaks) {
        bucketedData = removeBreak(bucketedData, br, buildEmptyBucketData);
    }

    return bucketedData;
}

function removeBreak<T extends BucketedDataItem>(
    bucketedData: T[],
    br: TimeSeriesDisableRange,
    buildEmptyBucketData: (tartTime: number, endTime: number) => Partial<T>
): T[] {
    const startBucket = findBucketIncludingTime(bucketedData, br.from);
    const endBucket = findBucketIncludingTime(bucketedData, br.to);

    if (startBucket === undefined && endBucket === undefined) {
        // Ignore
    } else if (startBucket === endBucket) {
        // Break is completely within bucket so we split it...

        // Add new bucket after break...
        bucketedData.push(
            buildEmptyBucket(
                br.to,
                startBucket!.endTime,
                startBucket!.endTimeDisplay,
                buildEmptyBucketData
            )
        );

        // Truncate original bucket...
        startBucket!.endTime = br.from;
        calculateBucketMidTime(startBucket!);
    } else {
        // Truncate the end of the bucket that the break start falls within...
        if (startBucket) {
            startBucket.endTime = br.from;
            calculateBucketMidTime(startBucket);
        }

        // Truncate the start of the bucket that the break end falls within...

        if (endBucket) {
            endBucket.startTime = br.to;
            calculateBucketMidTime(endBucket);
        }
    }

    // Remove buckets completely enclosed by the break...
    bucketedData = bucketedData.filter((b) => !isTotalEnclosedByBreak(b, br));

    return sortBy(bucketedData, 'startTime');
}

function findBucketIncludingTime<T extends BucketedDataItem>(
    bucketedData: T[],
    time: number
): T | undefined {
    return bucketedData.find((b) => b.startTime <= time && b.endTime >= time);
}

function isTotalEnclosedByBreak<T extends BucketedDataItem>(
    bucket: T,
    br: TimeSeriesDisableRange
): boolean {
    return bucket.startTime > br.from && bucket.endTime < br.to;
}

export const findNextBucket = <T extends BucketedDataItem>(data: T[], b: T) =>
    data.find((n) => n.startTime > b.endTime);

export function bucketData<
    TItem extends KeyedStreamItem,
    TBucket extends BucketedDataItem,
>(
    bucketSizeInMinutes: number,
    sortedData: TItem[],
    breaks: TimeSeriesDisableRange[],
    calculateBucketedValues: (bucket: TBucket, items: TItem[]) => void,
    buildEmptyBucketData: () => Partial<TBucket> = () => ({}),
    suppressFirstBucketRounding = false
): TBucket[] {
    const buckets = createEmptyBuckets<TBucket>(
        bucketSizeInMinutes,
        sortedData,
        breaks,
        buildEmptyBucketData,
        suppressFirstBucketRounding
    );

    type BucketItems = { bucket: TBucket; items: TItem[] };
    const bucketItems: { [midTime: number]: BucketItems } = {};

    let currentBucket: TBucket | undefined = buckets[0];

    for (const item of sortedData) {
        if (!currentBucket) {
            break;
        }
        const dataTime = getItemTime(item);

        if (dataTime > currentBucket.endTime) {
            currentBucket = findNextBucket(buckets, currentBucket!)!;
        }

        if (!bucketItems[currentBucket.midTime]) {
            bucketItems[currentBucket.midTime] = {
                bucket: currentBucket,
                items: [],
            };
        }
        bucketItems[currentBucket.midTime].items.push(item);
    }

    for (const item of Object.values(bucketItems)) {
        calculateBucketedValues(item.bucket, item.items);
    }

    return buckets;
}

export function getBucketAtTime<T extends BucketedDataItem>(
    sortedBucketedData: T[] | undefined,
    time?: number,
    returnLastBucketIfNotFound = true
): T | undefined {
    if (!sortedBucketedData) {
        return undefined;
    }

    const lastBucket = sortedBucketedData[sortedBucketedData.length - 1];
    const fallback = returnLastBucketIfNotFound ? lastBucket : undefined;

    if (time === undefined) {
        return fallback;
    }

    const bucketsStartingBefore = sortedBucketedData.filter(
        (x) => x.startTime <= time
    );
    const lastBucketStartingBefore = bucketsStartingBefore.reverse()[0];

    return lastBucketStartingBefore ?? fallback;
}
